// Reading a metered consumption file the way Greek users actually have it.
//
// The old importer took "the last number on each line" and assumed the first
// line was 1 January 00:00. Two ordinary Greek files broke it without a word:
//   - a file saved by Greek Excel (";" between columns, decimal comma): 0,37 was
//     split at the comma and read as 37, and the study ran on 30x the load;
//   - a meter year from July to June: the dates were ignored, so July landed on
//     January and every season was shifted by six months.
//
// This module reads the file instead of guessing:
//   - column separator, decimal mark and thousands grouping are detected, and
//     the user can override each one;
//   - dates are read (2025-07-01 00:00, 01/07/2025 00:00, 1/7/2025 0:00, date and
//     time in separate columns, one row per day with 24 or 96 values) and every
//     value is placed on its own month, day and time of the simulated year, so
//     any 12 consecutive months work;
//   - the clock change is handled (missing March hour filled, repeated October
//     hour averaged), short gaps are filled and reported, long ones stop the
//     import with the months named;
//   - values can be energy per interval (kWh) or average power (kW);
//   - nothing is used until the user has seen how the file was read.
//
// The output is what the engine has always taken: a calendar-ordered year of
// values (8,760 / 8,784 hourly or 35,040 / 35,136 quarter-hours) plus its
// resolution, so nothing changes on the backend.
//
// Every message comes as { el, en } so the screen speaks the user's language.

export const HOURS_NON_LEAP = 8760;
export const HOURS_LEAP = 8784;

const MINUTE = 60 * 1000;
const DAY = 24 * 60 * MINUTE;
const FULL_YEAR_COUNTS = {
  8760: { step: 60, leap: false },
  8784: { step: 60, leap: true },
  35040: { step: 15, leap: false },
  35136: { step: 15, leap: true },
};
// More missing than this and the year is not "measured" any more.
const MAX_MISSING_SHARE = 0.02;
// Gaps up to this long are bridged by a straight line; longer ones borrow the
// same time from the neighbouring week, which keeps the weekday pattern.
const MAX_INTERPOLATED_MINUTES = 180;

export const MONTHS_EL_SHORT = ["Ιαν", "Φεβ", "Μαρ", "Απρ", "Μάι", "Ιουν", "Ιουλ", "Αυγ", "Σεπ", "Οκτ", "Νοε", "Δεκ"];
export const MONTHS_EN_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_EL = ["Ιανουάριος", "Φεβρουάριος", "Μάρτιος", "Απρίλιος", "Μάιος", "Ιούνιος",
  "Ιούλιος", "Αύγουστος", "Σεπτέμβριος", "Οκτώβριος", "Νοέμβριος", "Δεκέμβριος"];
const MONTHS_EN = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

export const DEFAULT_IMPORT_OPTIONS = {
  delimiter: "auto", // auto | ";" | "," | "\t"
  decimal: "auto", // auto | "," | "."
  unit: "auto", // auto | kwh | kw
  valueColumn: "auto", // auto | column index
  targetYear: null, // the simulated year; null = previous calendar year
};

export const DELIMITER_LABELS = {
  ";": { el: "Ερωτηματικό «;»", en: "Semicolon «;»" },
  ",": { el: "Κόμμα «,»", en: "Comma «,»" },
  "\t": { el: "Tab", en: "Tab" },
};
export const DECIMAL_LABELS = {
  ",": { el: "Κόμμα (0,37)", en: "Comma (0,37)" },
  ".": { el: "Τελεία (0.37)", en: "Point (0.37)" },
};
export const UNIT_LABELS = {
  kwh: { el: "kWh ανά διάστημα (ενέργεια)", en: "kWh per interval (energy)" },
  kw: { el: "kW (μέση ισχύς του διαστήματος)", en: "kW (average power in the interval)" },
};

const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
const pad = (n) => String(n).padStart(2, "0");
export const formatThousands = (value, digits = 0) =>
  Number(value).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
const formatWall = (ms) => {
  const d = new Date(ms);
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
};
const msg = (el, en) => ({ el, en });

// ---------------------------------------------------------------------------
// Bytes -> text. Greek Excel saves "CSV (comma delimited)" in Windows-1253,
// "CSV UTF-8" with a BOM, and "Unicode text" as UTF-16 with tabs.
export function decodeLoadProfileBytes(buffer) {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const decode = (label, fatal = false) => new TextDecoder(label, { fatal }).decode(bytes);
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return { text: decode("utf-16le").replace(/^﻿/, ""), encoding: "utf-16le" };
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return { text: decode("utf-16be").replace(/^﻿/, ""), encoding: "utf-16be" };
  try {
    return { text: decode("utf-8", true).replace(/^﻿/, ""), encoding: "utf-8" };
  } catch (err) {
    try {
      return { text: decode("windows-1253"), encoding: "windows-1253" };
    } catch (err2) {
      return { text: decode("utf-8"), encoding: "utf-8" };
    }
  }
}

// ---------------------------------------------------------------------------
// Template. Opens in columns in Greek Excel (";" is its list separator), the
// BOM keeps the Greek header readable, and whatever Excel writes back on save
// (1/1/2025 0:00, decimal comma, Windows-1253) is read by readLoadProfile.
export function buildLoadProfileTemplate(resolution, year) {
  const y = Number(year) || new Date().getFullYear() - 1;
  const step = resolution === "15min" ? 15 : 60;
  const rows = ((isLeap(y) ? 366 : 365) * 24 * 60) / step;
  const lines = ["Ημερομηνία ώρα;kWh"];
  let t = Date.UTC(y, 0, 1);
  for (let i = 0; i < rows; i += 1) {
    lines.push(`${formatWall(t)};`);
    t += step * MINUTE;
  }
  return `﻿${lines.join("\r\n")}\r\n`;
}

// ---------------------------------------------------------------------------
// Low-level helpers

function splitLine(line, delimiter) {
  const out = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        quoted = !quoted;
      }
    } else if (ch === delimiter && !quoted) {
      out.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out;
}

function detectDelimiter(lines) {
  const sample = lines.slice(0, 400);
  const share = (ch) => sample.filter((l) => l.includes(ch)).length / Math.max(1, sample.length);
  if (share("\t") >= 0.5) return "\t";
  if (share(";") >= 0.5) return ";";
  return ",";
}

function parseNumber(raw, decimal) {
  if (raw === undefined || raw === null) return null;
  let s = String(raw).replace(/[\s '"]/g, "");
  if (!s) return null;
  if (!/^[-+]?[\d.,]+(e[-+]?\d+)?$/i.test(s)) return null;
  if (decimal === ",") {
    if (/^[-+]?\d{1,3}(\.\d{3})+(,\d+)?$/.test(s)) s = s.replace(/\./g, "");
    if ((s.match(/,/g) || []).length > 1) return null;
    s = s.replace(",", ".");
  } else {
    if (/^[-+]?\d{1,3}(,\d{3})+(\.\d+)?$/.test(s)) s = s.replace(/,/g, "");
    if (s.includes(",")) return null;
  }
  if ((s.match(/\./g) || []).length > 1) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

const ISO_RE = /(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T\s,]+(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?)?\s*(Z|[+-]\d{2}:?\d{2})?/i;
const DMY_RE = /(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})(?:[T\s,]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/;
const TIME_RE = /^(\d{1,2}):(\d{2})(?::\d{2})?(?:\s*[-–]\s*\d{1,2}:\d{2}(?::\d{2})?)?$/;

let athensFormatter = null;
function utcToAthensWall(msUtc) {
  if (!athensFormatter) {
    athensFormatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Athens", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    });
  }
  const parts = {};
  athensFormatter.formatToParts(new Date(msUtc)).forEach((p) => { parts[p.type] = Number(p.value); });
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
}

// A date (and possibly a time) found in a field. Returns the wall-clock time as
// a UTC-based millisecond value -- the file's own clock, no timezone shifts --
// plus whether a time of day was present.
function parseDateField(field, dateOrder) {
  let m = ISO_RE.exec(field);
  if (m) {
    const [, y, mo, d, h, mi, , tz] = m;
    if (Number(mo) < 1 || Number(mo) > 12 || Number(d) < 1 || Number(d) > 31) return null;
    let ms = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h || 0), Number(mi || 0));
    if (tz && h !== undefined) {
      let offset = 0;
      if (tz.toUpperCase() !== "Z") {
        const sign = tz[0] === "-" ? -1 : 1;
        const digits = tz.slice(1).replace(":", "");
        offset = sign * (Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2, 4))) * MINUTE;
      }
      ms = utcToAthensWall(ms - offset);
    }
    return { ms, hasTime: h !== undefined };
  }
  m = DMY_RE.exec(field);
  if (m) {
    const [, a, b, y, h, mi] = m;
    const day = dateOrder === "mdy" ? Number(b) : Number(a);
    const month = dateOrder === "mdy" ? Number(a) : Number(b);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return { ms: Date.UTC(Number(y), month - 1, day, Number(h || 0), Number(mi || 0)), hasTime: h !== undefined };
  }
  return null;
}

function parseTimeField(field) {
  const m = TIME_RE.exec(String(field || "").trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 24 || mi > 59) return null;
  return (h * 60 + mi) * MINUTE;
}

function detectDateOrder(rows) {
  let dmy = false;
  let mdy = false;
  rows.slice(0, 2000).forEach((fields) => {
    fields.forEach((f) => {
      if (ISO_RE.test(f)) return;
      const m = DMY_RE.exec(f);
      if (!m) return;
      if (Number(m[1]) > 12) dmy = true;
      if (Number(m[2]) > 12) mdy = true;
    });
  });
  return mdy && !dmy ? "mdy" : "dmy";
}

const CONSUMPTION_RE = /(kwh|κατανάλ|καταναλ|consum|import|απορρ|φορτ|load|ενέργ|energy|ποσότ|value|τιμή|active|ενεργ)/i;
const NOT_CONSUMPTION_RE = /(έγχυσ|εγχυσ|inject|export|παραγ|produc|generat|άεργ|αεργ|reactive|kvar|ισχύς\s*αιχμ|status|flag|κατάστ|ποιότ|quality)/i;

function columnName(header, index) {
  const name = header && header[index] !== undefined ? String(header[index]).trim() : "";
  return name || null;
}

// ---------------------------------------------------------------------------
// Main entry point.
export function readLoadProfile(text, options = {}) {
  const opts = { ...DEFAULT_IMPORT_OPTIONS, ...options };
  const targetYear = Number(opts.targetYear) || new Date().getFullYear() - 1;
  const errors = [];
  const warnings = [];
  const notes = [];
  const detected = {
    delimiter: null, decimal: null, layout: null, hasTimestamps: false, dateOrder: null,
    step: null, intervalEnding: false, period: null, columns: [], valueColumn: null,
    unit: null, targetYear, header: null,
  };
  const fail = (message) => {
    errors.push(message);
    return { ok: false, errors, warnings, notes, detected, values: [], resolution: null };
  };

  let lines = String(text || "").replace(/^﻿/, "").split(/\r\n|\n|\r/).map((l) => l.trim());
  lines = lines.filter((l) => l.length > 0);
  let sepHint = null;
  if (lines.length && /^sep=.$/i.test(lines[0])) {
    sepHint = lines[0].slice(4);
    lines = lines.slice(1);
  }
  if (!lines.length) return fail(msg("Το αρχείο είναι κενό.", "The file is empty."));

  const delimiter = opts.delimiter !== "auto" ? opts.delimiter : (sepHint || detectDelimiter(lines));
  detected.delimiter = delimiter;
  const split = lines.map((l) => splitLine(l, delimiter));
  const dateOrder = detectDateOrder(split);
  detected.dateOrder = dateOrder;

  // Which lines carry data? A line with a date, or (for a plain column of
  // values) a line whose fields are numbers. Everything before the first data
  // line is a header; the last of those names the columns.
  const dateInfo = split.map((fields) => {
    for (let i = 0; i < fields.length; i += 1) {
      const parsed = parseDateField(fields[i], dateOrder);
      if (parsed) return { index: i, ...parsed };
    }
    return null;
  });
  const datedCount = dateInfo.filter(Boolean).length;
  const hasTimestamps = datedCount >= Math.max(24, 0.5 * lines.length);
  detected.hasTimestamps = hasTimestamps;

  const looseNumeric = (f) => parseNumber(f, ",") !== null || parseNumber(f, ".") !== null;
  let firstData = -1;
  for (let i = 0; i < split.length; i += 1) {
    const isData = hasTimestamps ? Boolean(dateInfo[i]) : split[i].some(looseNumeric);
    if (isData) {
      firstData = i;
      break;
    }
  }
  if (firstData < 0) {
    return fail(msg("Δεν βρέθηκαν αριθμητικές τιμές στο αρχείο.", "No numeric values were found in the file."));
  }
  const header = firstData > 0 ? split[firstData - 1] : null;
  detected.header = header;
  const dataIdx = [];
  for (let i = firstData; i < split.length; i += 1) {
    if (hasTimestamps ? dateInfo[i] : split[i].some(looseNumeric)) dataIdx.push(i);
  }
  const skippedLines = lines.length - dataIdx.length;

  // Which fields hold the date and the time?
  let dateCol = null;
  let timeCol = null;
  if (hasTimestamps) {
    const counts = {};
    dataIdx.slice(0, 500).forEach((i) => {
      const k = dateInfo[i].index;
      counts[k] = (counts[k] || 0) + 1;
    });
    dateCol = Number(Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0]);
    const sample = dataIdx.slice(0, 200).filter((i) => dateInfo[i].index === dateCol);
    const withoutTime = sample.filter((i) => !dateInfo[i].hasTime);
    if (withoutTime.length > 0.5 * sample.length) {
      const next = dateCol + 1;
      const timed = withoutTime.filter((i) => parseTimeField(split[i][next]) !== null).length;
      if (timed > 0.5 * withoutTime.length) timeCol = next;
    }
  }
  const valueFieldsOf = (fields) => fields.filter((_, k) => k !== dateCol && k !== timeCol);

  // Decimal mark.
  let decimal = opts.decimal !== "auto" ? opts.decimal : null;
  let mergeDecimalPairs = false;
  const sampleRows = dataIdx.slice(0, 2000).map((i) => split[i]);
  if (delimiter === ",") {
    // A comma cannot be both. If it is the column separator, a decimal comma
    // shows up as one field too many whose last two parts are whole numbers.
    const expected = header ? header.length : null;
    const counts = sampleRows.map((f) => f.length);
    const minCount = Math.min(...counts);
    const isPair = (f) => /^-?\d+$/.test(f[f.length - 2] || "") && /^\d+$/.test(f[f.length - 1] || "");
    const base = expected !== null ? expected : minCount;
    const extra = sampleRows.filter((f) => f.length === base + 1 && isPair(f)).length;
    const mixed = counts.some((c) => c === base) && extra > 0;
    const anyPoint = sampleRows.some((f) => valueFieldsOf(f).some((v) => /\d\.\d/.test(v)));
    if (decimal === ",") {
      mergeDecimalPairs = true;
    } else if (decimal === null) {
      if (expected !== null && extra > 0) mergeDecimalPairs = true;
      else if (expected === null && mixed) mergeDecimalPairs = true;
      else if (expected === null && !anyPoint && sampleRows.every((f) => f.length >= 2 && isPair(f))
        && !sampleRows.some((f) => f[f.length - 1].length > 3)) {
        return fail(msg(
          "Δεν είναι σαφές αν το κόμμα χωρίζει στήλες ή είναι υποδιαστολή (π.χ. «0,37»). Ορίστε τα «Δεκαδικά» παραπάνω.",
          "It is not clear whether the comma separates columns or is a decimal mark (e.g. \"0,37\"). Set \"Decimals\" above."
        ));
      }
      decimal = mergeDecimalPairs ? "," : ".";
    }
  } else if (decimal === null) {
    const commaDecimal = sampleRows.some((f) => valueFieldsOf(f).some((v) => /^[-+]?\d[\d.]*,\d+$/.test(v.replace(/\s/g, ""))));
    decimal = commaDecimal ? "," : ".";
  }
  detected.decimal = decimal;

  const rowValues = (fields) => {
    let f = fields;
    if (mergeDecimalPairs) {
      const base = header ? header.length : null;
      const isPair = /^-?\d+$/.test(f[f.length - 2] || "") && /^\d+$/.test(f[f.length - 1] || "");
      if (isPair && (base === null ? f.length > 2 : f.length === base + 1)) {
        f = [...f.slice(0, -2), `${f[f.length - 2]},${f[f.length - 1]}`];
      }
    }
    return f;
  };

  // Layout: one row per interval, or one row per day with 24 / 96 values.
  const numericIdx = (fields) => fields
    .map((v, k) => (k !== dateCol && k !== timeCol && parseNumber(v, decimal) !== null ? k : -1))
    .filter((k) => k >= 0);
  let layout = hasTimestamps ? "rows" : "column";
  let dailyHasTotal = false;
  if (hasTimestamps) {
    const perRow = sampleRows.slice(0, 200).map((f) => numericIdx(rowValues(f)).length);
    const sorted = [...perRow].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)] || 0;
    if (median >= 23 && timeCol === null) layout = "daily";
    if (layout === "daily") {
      const freq = {};
      perRow.forEach((n) => { freq[n] = (freq[n] || 0) + 1; });
      const mode = Number(Object.keys(freq).sort((a, b) => freq[b] - freq[a])[0]);
      // 25 or 97 values on most days: the last one is a daily total.
      dailyHasTotal = mode === 25 || mode === 97;
    }
  }
  detected.layout = layout;

  // Value column (rows / column layouts).
  let valueCol = null;
  if (layout !== "daily") {
    const rows = sampleRows.map(rowValues);
    const width = Math.max(...rows.map((f) => f.length));
    const candidates = [];
    for (let k = 0; k < width; k += 1) {
      if (k === dateCol || k === timeCol) continue;
      const numeric = rows.filter((f) => parseNumber(f[k], decimal) !== null).length;
      const present = rows.filter((f) => f[k] !== undefined && f[k] !== "").length;
      if (present && numeric >= 0.9 * present && present >= 0.5 * rows.length) candidates.push(k);
    }
    detected.columns = candidates.map((k) => ({ index: k, name: columnName(header, k) }));
    if (opts.valueColumn !== "auto" && candidates.includes(Number(opts.valueColumn))) {
      valueCol = Number(opts.valueColumn);
    } else if (candidates.length) {
      const named = (re) => candidates.filter((k) => re.test(columnName(header, k) || ""));
      const good = named(CONSUMPTION_RE).filter((k) => !NOT_CONSUMPTION_RE.test(columnName(header, k) || ""));
      const notBad = candidates.filter((k) => !NOT_CONSUMPTION_RE.test(columnName(header, k) || ""));
      valueCol = good[0] ?? (notBad.length ? notBad[notBad.length - 1] : candidates[candidates.length - 1]);
    }
    if (valueCol === null) {
      const emptyValues = hasTimestamps && rows.every((f) => valueFieldsOf(f).every((v) => v === ""));
      return fail(emptyValues
        ? msg(
          "Η στήλη τιμών είναι κενή. Αν χρησιμοποιήσατε το πρότυπο, συμπληρώστε τη στήλη kWh και αποθηκεύστε το ξανά ως CSV.",
          "The value column is empty. If you used the template, fill in the kWh column and save it again as CSV."
        )
        : msg("Δεν βρέθηκε στήλη με αριθμητικές τιμές κατανάλωσης.", "No column of numeric consumption values was found."));
    }
    detected.valueColumn = valueCol;
  }

  // Unit.
  const unitHeader = layout === "daily" ? (header || []).join(" ") : (columnName(header, valueCol) || "");
  let unit = opts.unit;
  if (unit === "auto") unit = /kwh/i.test(unitHeader) ? "kwh" : /\bkw\b/i.test(unitHeader) ? "kw" : "kwh";
  detected.unit = unit;

  // ---------------------------------------------------------------- no dates
  if (!hasTimestamps) {
    const values = [];
    let unreadable = 0;
    dataIdx.forEach((i) => {
      const v = parseNumber(rowValues(split[i])[valueCol], decimal);
      if (v === null) unreadable += 1;
      else values.push(v);
    });
    const shape = FULL_YEAR_COUNTS[values.length];
    if (!shape) {
      return fail(msg(
        `Το αρχείο δεν έχει ημερομηνίες και έχει ${formatThousands(values.length)} τιμές. Χωρίς ημερομηνίες χρειάζεται ακριβώς ένας χρόνος από 1 Ιανουαρίου: 8,760 ωριαίες τιμές ή 35,040 τιμές 15 λεπτών. Αλλιώς προσθέστε στήλη ημερομηνίας και ώρας, όπως στο πρότυπο.`,
        `The file has no dates and holds ${formatThousands(values.length)} values. Without dates exactly one year from 1 January is needed: 8,760 hourly or 35,040 quarter-hourly values. Otherwise add a date-and-time column, as in the template.`
      ));
    }
    if (unreadable) {
      warnings.push(msg(`${unreadable} γραμμές δεν διαβάστηκαν ως αριθμοί.`, `${unreadable} lines could not be read as numbers.`));
    }
    notes.push(msg(
      "Το αρχείο δεν έχει ημερομηνίες: η πρώτη τιμή θεωρείται 1 Ιανουαρίου 00:00 και οι υπόλοιπες ακολουθούν με τη σειρά.",
      "The file has no dates: the first value is taken as 1 January 00:00 and the rest follow in order."
    ));
    detected.step = shape.step;
    return finish(values, shape.step, { errors, warnings, notes, detected, unit, skippedLines });
  }

  // ------------------------------------------------------------- with dates
  const records = [];
  let unreadable = 0;
  dataIdx.forEach((i) => {
    const fields = rowValues(split[i]);
    const info = dateInfo[i];
    if (!info || info.index !== dateCol) {
      unreadable += 1;
      return;
    }
    let base = info.ms;
    if (timeCol !== null) {
      const t = parseTimeField(fields[timeCol]);
      if (t === null) {
        unreadable += 1;
        return;
      }
      base += t;
    }
    if (layout === "daily") {
      let nums = numericIdx(fields).map((k) => parseNumber(fields[k], decimal));
      if (dailyHasTotal) nums = nums.slice(0, -1);
      const n = nums.length;
      const perHour = n >= 92 ? 4 : 1;
      const stepMin = 60 / perHour;
      const nominal = 24 * perHour;
      nums.forEach((v, j) => {
        let slot = j;
        if (n === nominal - perHour && j >= 3 * perHour) slot = j + perHour; // spring: no 03:00
        if (n === nominal + perHour && j >= 4 * perHour) slot = j - perHour; // autumn: 03:00 twice
        records.push({ t: base + slot * stepMin * MINUTE, v });
      });
      return;
    }
    const v = parseNumber(fields[valueCol], decimal);
    if (v === null) {
      unreadable += 1;
      return;
    }
    records.push({ t: base, v });
  });
  if (unreadable) {
    warnings.push(msg(
      `${formatThousands(unreadable)} γραμμές με ημερομηνία δεν είχαν αριθμητική τιμή και παραλείφθηκαν.`,
      `${formatThousands(unreadable)} dated lines had no numeric value and were skipped.`
    ));
  }
  if (records.length < 48) {
    return fail(msg("Το αρχείο έχει πολύ λίγες μετρήσεις.", "The file holds too few readings."));
  }

  // Resolution from the spacing of the readings.
  const times = Array.from(new Set(records.map((r) => r.t))).sort((a, b) => a - b);
  const diffs = {};
  for (let i = 1; i < times.length; i += 1) {
    const d = Math.round((times[i] - times[i - 1]) / MINUTE);
    diffs[d] = (diffs[d] || 0) + 1;
  }
  const modal = Number(Object.keys(diffs).sort((a, b) => diffs[b] - diffs[a])[0]);
  if (![15, 30, 60].includes(modal)) {
    return fail(msg(
      `Οι μετρήσεις απέχουν ${modal} λεπτά μεταξύ τους. Υποστηρίζονται τιμές ανά 15 λεπτά, 30 λεπτά ή ώρα.`,
      `Readings are ${modal} minutes apart. Values every 15 minutes, 30 minutes or hour are supported.`
    ));
  }
  let step = modal;

  // Stamped at the END of each interval (01:00 for 00:00-01:00, 24:00 or the
  // next day's 00:00 for the last one)? Then move everything back one step.
  const minuteOfDay = (ms) => Math.round((ms % DAY) / MINUTE);
  const first = times[0];
  const last = times[times.length - 1];
  if (minuteOfDay(first) === step && minuteOfDay(last) === 0) {
    records.forEach((r) => { r.t -= step * MINUTE; });
    detected.intervalEnding = true;
    notes.push(msg(
      "Οι ώρες του αρχείου δηλώνουν το τέλος κάθε διαστήματος (π.χ. 01:00 για 00:00–01:00) και μετακινήθηκαν στην αρχή του.",
      "The file stamps the end of each interval (e.g. 01:00 for 00:00–01:00); values were moved to the start of their interval."
    ));
  }

  // Half-hourly: add pairs into hours.
  if (step === 30) {
    records.forEach((r) => { r.t -= r.t % (60 * MINUTE); });
    step = 60;
    notes.push(msg("Οι τιμές μισής ώρας αθροίστηκαν ανά ώρα.", "Half-hourly values were summed per hour."));
  }
  detected.step = step;

  // kW -> kWh per interval.
  const toEnergy = unit === "kw" ? modal / 60 : 1;

  // Keep the most recent 12 months.
  records.sort((a, b) => a.t - b.t);
  const lastT = records[records.length - 1].t;
  const lastD = new Date(lastT);
  const windowStart = Date.UTC(lastD.getUTCFullYear() - 1, lastD.getUTCMonth(), lastD.getUTCDate(),
    lastD.getUTCHours(), lastD.getUTCMinutes()) + (modal === 30 ? 60 : modal) * MINUTE;
  const firstT = records[0].t;
  if (firstT < windowStart - modal * MINUTE) {
    notes.push(msg(
      `Το αρχείο καλύπτει ${formatWall(firstT)} – ${formatWall(lastT)}. Χρησιμοποιήθηκαν οι τελευταίοι 12 μήνες (${formatWall(windowStart)} – ${formatWall(lastT)}).`,
      `The file covers ${formatWall(firstT)} – ${formatWall(lastT)}. The last 12 months were used (${formatWall(windowStart)} – ${formatWall(lastT)}).`
    ));
  }
  const windowRecords = records.filter((r) => r.t >= windowStart);
  const periodStart = windowRecords[0].t;
  detected.period = { start: formatWall(periodStart), end: formatWall(lastT) };
  const spanDays = (lastT - periodStart) / DAY;
  if (spanDays < 365 * (1 - MAX_MISSING_SHARE) - 1) {
    return fail(msg(
      `Το αρχείο καλύπτει ${Math.round(spanDays + step / 1440)} ημέρες (${formatWall(periodStart)} – ${formatWall(lastT)}). Χρειάζονται 12 συνεχόμενοι μήνες μετρήσεων.`,
      `The file covers ${Math.round(spanDays + step / 1440)} days (${formatWall(periodStart)} – ${formatWall(lastT)}). Twelve consecutive months of readings are needed.`
    ));
  }

  // Place every reading on its month, day and time in the simulated year.
  const leapTarget = isLeap(targetYear);
  const slotsPerDay = (24 * 60) / step;
  const days = leapTarget ? 366 : 365;
  const total = days * slotsPerDay;
  const sum = new Float64Array(total);
  const cnt = new Uint16Array(total);
  const yearStart = Date.UTC(targetYear, 0, 1);
  let droppedLeapDay = 0;
  let sawLeapDay = false;
  windowRecords.forEach((r) => {
    const d = new Date(r.t);
    const month = d.getUTCMonth();
    const day = d.getUTCDate();
    if (month === 1 && day === 29) {
      sawLeapDay = true;
      if (!leapTarget) {
        droppedLeapDay += 1;
        return;
      }
    }
    const dayIndex = Math.round((Date.UTC(targetYear, month, day) - yearStart) / DAY);
    const slot = dayIndex * slotsPerDay + Math.floor((d.getUTCHours() * 60 + d.getUTCMinutes()) / step);
    if (slot < 0 || slot >= total) return;
    sum[slot] += r.v * toEnergy;
    cnt[slot] += 1;
  });

  let duplicates = 0;
  const values = new Array(total).fill(null);
  for (let i = 0; i < total; i += 1) {
    if (cnt[i]) {
      // Summed when half-hours were folded into an hour, averaged when the
      // same time appears twice (the repeated hour of the October clock change).
      const folded = modal === 30 ? 2 : 1;
      const repeats = cnt[i] / folded;
      if (repeats > 1) duplicates += cnt[i] - folded;
      values[i] = repeats > 1 ? sum[i] / repeats : sum[i];
    }
  }
  if (duplicates) {
    notes.push(msg(
      duplicates === 1
        ? "1 διπλή χρονική στιγμή (π.χ. η ώρα που επαναλαμβάνεται στην αλλαγή ώρας του Οκτωβρίου): κρατήθηκε ο μέσος όρος."
        : `${formatThousands(duplicates)} διπλές χρονικές στιγμές (π.χ. η ώρα που επαναλαμβάνεται στην αλλαγή ώρας του Οκτωβρίου): κρατήθηκε ο μέσος όρος τους.`,
      duplicates === 1
        ? "1 repeated timestamp (e.g. the hour repeated at the October clock change): the average was kept."
        : `${formatThousands(duplicates)} repeated timestamps (e.g. the hour repeated at the October clock change): their average was kept.`
    ));
  }
  if (leapTarget && !sawLeapDay) {
    const feb28 = (31 + 27) * slotsPerDay;
    const feb29 = (31 + 28) * slotsPerDay;
    for (let s = 0; s < slotsPerDay; s += 1) {
      if (values[feb29 + s] === null && values[feb28 + s] !== null) values[feb29 + s] = values[feb28 + s];
    }
    notes.push(msg(
      `Το έτος της μελέτης (${targetYear}) έχει 29 Φεβρουαρίου που δεν υπάρχει στο αρχείο· συμπληρώθηκε με τις τιμές της 28ης.`,
      `The study year (${targetYear}) has a 29 February the file lacks; it was filled with the 28th's values.`
    ));
  }
  if (droppedLeapDay) {
    notes.push(msg(
      `Η 29η Φεβρουαρίου του αρχείου δεν υπάρχει στο έτος της μελέτης (${targetYear}) και παραλείφθηκε.`,
      `The file's 29 February does not exist in the study year (${targetYear}) and was left out.`
    ));
  }

  // Missing readings.
  const missing = [];
  for (let i = 0; i < total; i += 1) if (values[i] === null) missing.push(i);
  if (missing.length > MAX_MISSING_SHARE * total) {
    const perMonth = new Array(12).fill(0);
    const daysIn = [31, leapTarget ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    missing.forEach((i) => {
      let dayIndex = Math.floor(i / slotsPerDay);
      let m = 0;
      while (dayIndex >= daysIn[m]) {
        dayIndex -= daysIn[m];
        m += 1;
      }
      perMonth[m] += 1;
    });
    const listEl = perMonth.map((c, m) => (c ? `${MONTHS_EL[m]} (${formatThousands(c / slotsPerDay, 1)} ημέρες)` : null)).filter(Boolean).join(", ");
    const listEn = perMonth.map((c, m) => (c ? `${MONTHS_EN[m]} (${formatThousands(c / slotsPerDay, 1)} days)` : null)).filter(Boolean).join(", ");
    return fail(msg(
      `Λείπουν ${formatThousands((missing.length * step) / 60)} ώρες μετρήσεων (${formatThousands((missing.length / total) * 100, 1)}% του έτους): ${listEl}. Χρειάζονται 12 πλήρεις μήνες.`,
      `${formatThousands((missing.length * step) / 60)} hours of readings are missing (${formatThousands((missing.length / total) * 100, 1)}% of the year): ${listEn}. Twelve complete months are needed.`
    ));
  }
  if (missing.length) {
    const known = values.slice();
    const maxRun = Math.round(MAX_INTERPOLATED_MINUTES / step);
    let i = 0;
    while (i < total) {
      if (known[i] !== null) {
        i += 1;
        continue;
      }
      let j = i;
      while (j < total && known[j] === null) j += 1;
      const before = i > 0 ? known[i - 1] : null;
      const after = j < total ? known[j] : null;
      for (let k = i; k < j; k += 1) {
        if (j - i <= maxRun && before !== null && after !== null) {
          values[k] = before + ((after - before) * (k - i + 1)) / (j - i + 1);
        } else {
          let fill = null;
          for (let w = 1; w <= 4 && fill === null; w += 1) {
            for (const dir of [-1, 1]) {
              const o = k + dir * w * 7 * slotsPerDay;
              if (o >= 0 && o < total && known[o] !== null) {
                fill = known[o];
                break;
              }
            }
          }
          values[k] = fill !== null ? fill : (before ?? after ?? 0);
        }
      }
      i = j;
    }
    const h = (missing.length * step) / 60;
    const hEl = h === 1 ? "1 ώρα" : `${formatThousands(h, Number.isInteger(h) ? 0 : 2)} ώρες`;
    const hEn = h === 1 ? "1 hour" : `${formatThousands(h, Number.isInteger(h) ? 0 : 2)} hours`;
    notes.push(msg(
      `${h === 1 ? "Συμπληρώθηκε" : "Συμπληρώθηκαν"} ${hEl} χωρίς μέτρηση (π.χ. η ώρα που χάνεται στην αλλαγή ώρας του Μαρτίου).`,
      `${hEn} without a reading ${h === 1 ? "was" : "were"} filled (e.g. the hour lost at the March clock change).`
    ));
  }

  notes.unshift(msg(
    `Οι τιμές τοποθετήθηκαν με βάση την ημερομηνία τους στο έτος της μελέτης (${targetYear}, Ιανουάριος–Δεκέμβριος).`,
    `Values were placed by their date in the study year (${targetYear}, January–December).`
  ));
  return finish(values, step, { errors, warnings, notes, detected, unit, skippedLines });
}

// Annual energy, peaks, load factor and kWh per calendar month of a stored
// profile. 15-minute values are summed per hour: they are energy per interval.
export function summarizeProfileValues(rawValues, resolutionHint) {
  const values = (Array.isArray(rawValues) ? rawValues : []).map((v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
  });
  const resolution = resolutionHint === "15min" || resolutionHint === "hourly"
    ? resolutionHint
    : (values.length > HOURS_LEAP ? "15min" : "hourly");
  const perHour = resolution === "15min" ? 4 : 1;
  const hourly = perHour === 1 ? values : Array.from({ length: Math.floor(values.length / 4) }, (_, i) =>
    values[i * 4] + values[i * 4 + 1] + values[i * 4 + 2] + values[i * 4 + 3]);
  const annualKwh = hourly.reduce((a, b) => a + b, 0);
  let peakKw = 0;
  hourly.forEach((v) => { if (v > peakKw) peakKw = v; });
  let nativePeakKw = 0;
  values.forEach((v) => { if (v * perHour > nativePeakKw) nativePeakKw = v * perHour; });
  const loadFactorPct = peakKw > 0 && hourly.length ? (annualKwh / (peakKw * hourly.length)) * 100 : 0;
  const leap = hourly.length >= HOURS_LEAP;
  const daysIn = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const monthlyKwh = [];
  let cursor = 0;
  daysIn.forEach((d) => {
    let sum = 0;
    for (let h = cursor; h < cursor + d * 24 && h < hourly.length; h += 1) sum += hourly[h];
    monthlyKwh.push(sum);
    cursor += d * 24;
  });
  return {
    resolution,
    annualKwh,
    peakKw,
    nativePeakKw,
    loadFactorPct,
    monthlyKwh,
    wasAggregated: perHour === 4 && hourly.length > 0,
    peakSmoothingPct: nativePeakKw > 0 ? ((nativePeakKw - peakKw) / nativePeakKw) * 100 : 0,
  };
}

function finish(rawValues, step, ctx) {
  const { errors, warnings, notes, detected, unit, skippedLines } = ctx;
  let negatives = 0;
  const values = rawValues.map((v) => {
    let n = Number(v) || 0;
    if (!detected.hasTimestamps && unit === "kw") n *= step / 60;
    if (n < 0) {
      negatives += 1;
      n = 0;
    }
    return Math.round(n * 10000) / 10000;
  });
  if (negatives) {
    warnings.push(msg(
      `${formatThousands(negatives)} αρνητικές τιμές μηδενίστηκαν. Συνήθως σημαίνει ότι διαβάστηκε στήλη έγχυσης· ελέγξτε τη «Στήλη τιμών».`,
      `${formatThousands(negatives)} negative values were set to zero. This usually means an injection column was read; check "Value column".`
    ));
  }
  const resolution = step === 15 ? "15min" : "hourly";
  const { annualKwh, peakKw, nativePeakKw, loadFactorPct, monthlyKwh, wasAggregated, peakSmoothingPct } =
    summarizeProfileValues(values, resolution);

  if (annualKwh <= 0) {
    errors.push(msg("Όλες οι τιμές είναι μηδέν· δεν υπάρχει κατανάλωση να μελετηθεί.", "Every value is zero, so there is no load to study."));
  } else if (loadFactorPct < 5) {
    warnings.push(msg(
      `Ο συντελεστής φορτίου είναι μόλις ${formatThousands(loadFactorPct, 1)}%, κάτι πολύ ασυνήθιστο. Ελέγξτε τη μονάδα (kWh ή kW) και τα δεκαδικά.`,
      `The load factor is only ${formatThousands(loadFactorPct, 1)}%, which is very unusual. Check the unit (kWh or kW) and the decimals.`
    ));
  }

  return {
    ok: errors.length === 0,
    values: errors.length ? [] : values,
    resolution,
    count: values.length,
    annualKwh,
    peakKw,
    nativePeakKw,
    wasAggregated,
    peakSmoothingPct,
    loadFactorPct,
    monthlyKwh,
    skippedLines,
    errors,
    warnings,
    notes,
    detected,
  };
}

export const columnLabel = (col, isGreek) => (col.name
  ? `${isGreek ? "στήλη" : "column"} «${col.name}»`
  : `${isGreek ? "στήλη" : "column"} ${col.index + 1}`);

// One sentence: how the file was read. Shown before the user accepts it.
export function describeReading(result, isGreek) {
  const d = result?.detected;
  if (!d) return "";
  const parts = [];
  const res = result.resolution === "15min" ? (isGreek ? "τιμές 15 λεπτών" : "15-minute values")
    : (isGreek ? "ωριαίες τιμές" : "hourly values");
  parts.push(res);
  if (d.layout === "daily") parts.push(isGreek ? "μία γραμμή ανά ημέρα" : "one row per day");
  if (d.delimiter) parts.push(`${isGreek ? "διαχωριστικό" : "separator"} ${d.delimiter === "\t" ? "Tab" : `«${d.delimiter}»`}`);
  if (d.decimal) parts.push(d.decimal === "," ? (isGreek ? "δεκαδικά με κόμμα" : "decimal comma") : (isGreek ? "δεκαδικά με τελεία" : "decimal point"));
  if (d.valueColumn !== null && d.columns.length) {
    const col = d.columns.find((c) => c.index === d.valueColumn);
    if (col) parts.push(columnLabel(col, isGreek));
  }
  if (d.unit) {
    parts.push(d.unit === "kw"
      ? (isGreek ? "τιμές σε kW (μετατράπηκαν σε kWh)" : "values in kW (converted to kWh)")
      : (isGreek ? "τιμές σε kWh" : "values in kWh"));
  }
  if (d.period) parts.push(`${isGreek ? "περίοδος" : "period"} ${d.period.start} – ${d.period.end}`);
  return parts.join(" · ");
}
